import { ProjectNotFound } from '@/errors/projectNotFound';
import { useAuth, useAuthToken } from '@/services/auth';
import { client as userClient } from '@/services/user';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import { apiBasePath } from '@/utils/apiBasePath';
import { Log } from '@devanjs/log';
import { faBoxesStacked, faCodeSimple, faWebhook } from '@fortawesome/pro-light-svg-icons';
import { useAccess } from '@teemill/modules/authorize';
import { useMenu } from '@teemill/modules/menu';
import { isHttpError } from '@teemill/modules/utils';
import type {
  ArchiveTasksRequest,
  BulkUpdateTasksRequest,
  CreateBookmarkRequest,
  CreateInviteRequest,
  CreateProjectRequest,
  CreateTaskRequest,
  CreateUserGroupRequest,
  GenerateBrandContextRequest,
  InstallTemplateRequest,
  Project,
  ProjectsApiGetIntegrationsRequest,
  TemplateCode,
  UpdateBookmarkRequest,
  UpdateBookmarkTabsRequest,
  UpdateIntegrationRequest,
  UpdateProjectRequest,
  UpdateTaskRequest,
  UpdateUserGroupRequest,
  UpdateUserRequest,
} from '@teemill/projects';
import { Configuration, ProjectsApi } from '@teemill/projects';
import { computedEager, watchImmediate, whenever } from '@vueuse/core';
import retry from 'async-retry';
import type { RawAxiosRequestConfig } from 'axios';
import { isString } from 'lodash';
import type { MaybeRef, Ref } from 'vue';
import { type App, computed, toValue } from 'vue';
import type { RouteLocationNormalized, Router } from 'vue-router';

const projectClient = new ProjectsApi(
  new Configuration({
    basePath: apiBasePath(),
    accessToken: async () => useAuthToken(),
    baseOptions: {
      headers: {
        'User-Agent': null,
      },
    },
  }),
);

export const useProjects = () => {
  const store = useProjectStore();

  return {
    create: (request: CreateProjectRequest) =>
      projectClient.createProject({ createProjectRequest: request }),
    get: (project: string) => projectClient.getProject({ project }),
    list: () => projectClient.getProjects(),
    update: (id: string, request: UpdateProjectRequest) =>
      projectClient.updateProject({ project: id, updateProjectRequest: request }),
    delete: (project: string) => projectClient.deleteProject({ project }),
    users: {
      get: (project: string, user: string) => projectClient.getUser({ project, user }),
      update: (project: string, user: string, request: UpdateUserRequest) =>
        projectClient.updateUser({ project, user, updateUserRequest: request }),
      delete: (project: string, user: string) => projectClient.deleteUser({ project, user }),
      list: (
        project: string,
        { search, pageToken, pageSize }: { search?: string; pageToken?: number; pageSize?: number },
      ) => projectClient.getUsers({ project, search, pageToken, pageSize }),
      reset2FA: (project: string, user: string) => projectClient.reset2FA({ project, user }),
      clients: {
        list: (project: string, user: string) => projectClient.getClients({ project, user }),
      },
      logs: {
        list: (project: string, user: string) => projectClient.getLogs({ project, user }),
      },
      invite: {
        get: (project: string, user: string) => projectClient.getUserInvite({ project, user }),
      },
    },
    invites: {
      create: (id: string, request: CreateInviteRequest) =>
        projectClient.createInvite({ project: id, createInviteRequest: request }),
      list: (
        project: string,
        { search, pageToken, pageSize }: { search?: string; pageToken?: number; pageSize?: number },
      ) => projectClient.getInvites({ project, search, pageToken, pageSize }),
      delete: (project: string, invite: string) => projectClient.deleteInvite({ project, invite }),
    },
    integrations: {
      get: (project: string, integration: string) =>
        projectClient.getIntegration({ project, integration }),
      list: (
        requestParameters: ProjectsApiGetIntegrationsRequest,
        options?: RawAxiosRequestConfig,
      ) => projectClient.getIntegrations(requestParameters, options),
      install: (project: string, integration: string) =>
        projectClient.installIntegration({ project, integration }),
      uninstall: (project: string, integration: string) =>
        projectClient.uninstallIntegration({ project, integration }),
      setup: (project: string, integration: string, options: UpdateIntegrationRequest) =>
        projectClient.setupIntegration({ project, integration, updateIntegrationRequest: options }),
      update: (project: string, integration: string, options: UpdateIntegrationRequest) =>
        projectClient.updateIntegration({
          project,
          integration,
          updateIntegrationRequest: options,
        }),
      generateBrandContext: (
        project: string,
        request: GenerateBrandContextRequest & { section: string },
      ) => projectClient.generateBrandContext({ project, generateBrandContextRequest: request }),
    },
    templates: {
      list: (project?: string) =>
        projectClient.getTemplates({ project }).then(({ data }) => data.templates),
      install: (project: string, template: TemplateCode, options: InstallTemplateRequest) =>
        projectClient.installTemplate({ project, template, installTemplateRequest: options }),
    },
    auth: {
      list: (project: string) => projectClient.getAuth({ project }),
      create: (project: string) => projectClient.createAuth({ project }),
      delete: (project: string, auth: string) => projectClient.deleteAuth({ project, auth }),
    },
    tasks: {
      list: (project: string) =>
        projectClient.listTasks({ project }).then(({ data }) => data.tasks),
      get: (project: string, id: string) =>
        projectClient.getTask({ project, id }).then(({ data }) => data),
      create: (project: string, request: CreateTaskRequest) =>
        projectClient.createTask({ project, createTaskRequest: request }).then(({ data }) => data),
      update: (project: string, id: string, request: UpdateTaskRequest) =>
        projectClient
          .updateTask({ project, id, updateTaskRequest: request })
          .then(({ data }) => data),
      delete: (project: string, id: string) => projectClient.deleteTask({ project, id }),
      export: (project: string) => projectClient.exportTasks({ project }),
      okrsExport: (project: string) => projectClient.exportOkrs({ project }),
      bulkUpdate: (project: string, request: BulkUpdateTasksRequest) =>
        projectClient
          .bulkUpdateTasks({ project, bulkUpdateTasksRequest: request })
          .then(({ data }) => data),
      okrsSummary: (project: string) => projectClient.getOkrSummary({ project }),
      archive: (project: string, request: ArchiveTasksRequest) =>
        projectClient.archiveTasks({ project, archiveTasksRequest: request }),
    },
    bookmarks: {
      list: (project: string) =>
        projectClient.listBookmarks({ project }).then(({ data }) => data.bookmarks),
      create: (project: string, data: CreateBookmarkRequest) =>
        projectClient
          .createBookmark({ project, createBookmarkRequest: data })
          .then(({ data }) => data),
      update: (project: string, id: string, request: UpdateBookmarkRequest) =>
        projectClient
          .updateBookmark({ project, id, updateBookmarkRequest: request })
          .then(({ data }) => data),
      delete: (project: string, id: string) => projectClient.deleteBookmark({ project, id }),
    },
    bookmarkTabs: {
      list: (project: string) => projectClient.listBookmarktabs({ project }),
      update: (project: string, request: UpdateBookmarkTabsRequest) =>
        projectClient
          .updateBookmarkTabs({ project, updateBookmarkTabsRequest: request })
          .then(({ data }) => data),
    },
    groups: {
      list: (project: string) => projectClient.listUserGroups({ project }),
      create: (project: string, request: CreateUserGroupRequest) =>
        projectClient.createUserGroup({ project, createUserGroupRequest: request }),
      get: (project: string, group: string) =>
        projectClient.getUserGroup({ project, userGroup: group }),
      update: (project: string, group: string, request: UpdateUserGroupRequest) =>
        projectClient.updateUserGroup({
          project,
          userGroup: group,
          updateUserGroupRequest: request,
        }),
      delete: (project: string, group: string) =>
        projectClient.deleteUserGroup({ project, userGroup: group }),
    },
    ready: computed(() => store.ready),
    project: computedEager(() => store.active),
    all: store.projects,
  };
};

// @ts-expect-error I have no idea how to fix you...
export const useProject: {
  (options: { id?: MaybeRef<string>; required: true }): Ref<Project>;
  (options?: { id?: MaybeRef<string>; required?: false }): Ref<Project | undefined>;
} = (options: { id?: MaybeRef<string>; required?: boolean } = { required: false }) => {
  const store = useProjectStore();

  return computedEager(() => {
    let project = undefined;

    if (options.id === undefined) {
      project = store.active;
    } else {
      project = store.projects.find((project) => project.id === toValue(options.id));
    }

    if (options.required === true && project === undefined) {
      throw new ProjectNotFound('Project not found');
    }

    return project;
  });
};

export const project = {
  async install(
    app: App,
    {
      router: appRouter,
    }: {
      router: Router;
    },
  ) {
    const authStore = useAuthStore();
    const projectStore = useProjectStore();
    const menu = useMenu();

    const menuItems = [
      {
        name: 'modules',
        title: 'Modules',
        to: { name: 'integrations.store' },
        icon: faBoxesStacked,
        order: 1000,
      },
      {
        name: 'developer',
        title: 'Developer',
        to: { name: 'developer.api.index' },
        icon: faCodeSimple,
        order: 10000,
      },
      {
        name: 'developer.api',
        title: 'APIs',
        to: { name: 'developer.api.index' },
        icon: faWebhook,
        order: 10000,
      },
    ];

    whenever(
      () => projectStore.active?.id,
      async (activeId) => {
        const { data } = await projectClient.getProject({
          project: activeId,
        });

        projectStore.update(data);

        const auth = useAuth();

        if (auth.isAuthenticatedAs('user')) {
          try {
            await retry(
              () =>
                userClient.getToken({
                  ...(data && { project: data.id }),
                }),
              {
                retries: 2,
                factor: 2,
                minTimeout: 1000,
                onRetry: (error: any, attempt: number) => {
                  if (isHttpError(error) && error.response?.status === 503) {
                    Log.tag('projects')
                      .yellow('Retry')
                      .info(`503 error on attempt ${attempt}, retrying...`);
                  }
                },
              },
            );
          } catch (error: any) {
            if (isHttpError(error) && [401, 419].includes(error.response?.status || 0)) {
              auth.unAuthenticate();
              appRouter.push({
                name: 'account.login',
                query: {
                  redirect: encodeURIComponent(appRouter.currentRoute.value.fullPath),
                },
              });
            } else if (
              isHttpError(error) &&
              error.response?.status === 403 &&
              error.response?.data?.code === '2FA_ACTION_REQUIRED'
            ) {
              window.location.href =
                '/settings/two-factor-authentication/setup-authenticator?reason=2fa_required';

              return '';
            }

            throw error;
          }
        }

        const { access } = useAccess(() => ['integrations.listings.list']);
        menu.addWhen(() => !!projectStore.active && access.value === 'granted', ...menuItems);
      },
      {
        immediate: true,
      },
    );

    watchImmediate(
      () => ({
        ready: authStore.ready,
        authenticated: authStore.isAuthenticated,
      }),
      async ({ ready, authenticated }) => {
        if (!ready) {
          return;
        }

        if (!authenticated) {
          projectStore.updateAll([]);
        } else {
          try {
            const { data } = await projectClient.getProjects({
              project: appRouter.currentRoute.value.query.project as string,
            });

            projectStore.updateAll(data.projects);
          } catch (error: any) {
            switch (error.response?.status) {
              case 401:
                projectStore.updateAll([]);
                break;

              case 403:
                projectStore.updateAll([]);
                projectStore.updateRedirect({ name: 'account.settings.2fa' });
                break;

              default:
                throw error;
            }
          }
        }
      },
    );

    // Some routes don't require an active project. This removes the project query from the url if necessary
    // appRouter.beforeResolve((to, from, next) => {
    //   if (to.meta.requireProject === false) {
    //     const query = to.query;

    //     if (query.project !== undefined) {
    //       console.log('beforeResolve', 'remove project query', to.query);
    //       delete query.project;
    //       next({
    //         ...to,
    //         query: query,
    //       });
    //     }

    //     return;
    //   }

    //   next();
    // });

    const activeProject = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      let project: string | undefined;

      if (project === undefined && isString(to.query.project)) {
        project = to.query.project;
      }

      if (project === undefined && isString(from.query.project)) {
        project = from.query.project;
      }

      if (project === undefined) {
        const storedProject = projectStore.projects[0]?.id;

        if (isString(storedProject)) {
          project = storedProject;
        }
      }

      return project;
    };

    appRouter.beforeEach(async (to, from, next) => {
      if (to.name === 'platform.loading') {
        next();
        return;
      }

      const projectQuery = activeProject(to, from);
      const projectExistsInState = projectQuery && !!projectStore.find(projectQuery);

      const projectRequired =
        [...to.matched.map((m) => m.meta?.requires?.project), to.meta.requires?.project]
          .filter((m) => m !== undefined)
          .map((m) => (typeof m === 'boolean' ? (m ? 'always' : 'never') : m))
          .at(-1) ?? 'always';

      Log.tag('project')
        .green('Routing')
        .info(`Project ${projectRequired} required for ${String(to.name)}`);

      if (!projectExistsInState && projectQuery) {
        try {
          const { data } = await projectClient.getProject({
            project: projectQuery,
          });

          projectStore.update(data);
        } catch (error: any) {
          if (projectRequired === 'always') {
            Log.tag('project').green('Routing').info('No project -> Redirecting to dashboard');

            next({
              name: 'dashboard',
              query: {
                ...to.query,
                project: undefined,
              },
            });
          } else {
            Log.tag('project').green('Routing').info('No active project - continuing');

            next();
          }

          return;
        }
      }

      if (
        (projectRequired === 'always' && to.query.project === undefined) ||
        (projectRequired === 'sometimes' && to.query.project === undefined && projectQuery)
      ) {
        Log.tag('project').green('Routing').info('Adding project query');

        next({
          ...to,
          query: {
            ...to.query,
            project: projectQuery,
          },
        });

        return;
      }

      if (projectRequired === 'never' && to.query.project !== undefined) {
        Log.tag('project').green('Routing').info('Removing project query');

        next({
          ...to,
          query: {
            ...to.query,
            project: undefined,
          },
        });

        return;
      }

      if (
        to.query.project !== undefined &&
        to.query.project !== from.query.project &&
        from.name !== 'projects.switch' &&
        to.name !== 'projects.switch'
      ) {
        Log.tag('project').green('Routing').space().lightBlue('Switch Required').info(to.fullPath);

        next({
          name: 'projects.switch',
          query: {
            project: projectQuery,
            redirect_uri: to.fullPath,
          },
          replace: true,
        });

        return;
      }

      Log.tag('project').green('Routing').info('Project', projectStore.active?.name);

      next();
    });
  },
};
